import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, Upload, Loader2, CheckCircle2, ArrowLeft, ShieldCheck, FileText } from "lucide-react";
import { Header } from "@/components/sections/Header";
import { Footer } from "@/components/sections/Footer";
import { StickyCallBar } from "@/components/sections/StickyCallBar";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POSITIONS = ["Cleaner / Janitorial", "Floor Tech", "Day Porter", "Handyman", "Supervisor"];
const PERKS = ["Flexible schedules (full & part-time)", "Paid training", "Growth opportunities", "Locally owned & supportive team"];

const emptyForm = {
  name: "", email: "", phone: "", position: "", bio: "",
  years_experience: "", availability: "", has_transportation: "", work_authorized: "",
  no_criminal_background: false,
};

export default function Careers() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Careers | Elite Facility Solutions — Join Our Cleaning Team";
    window.scrollTo(0, 0);
    return () => { document.title = "Elite Facility Solutions | Commercial Cleaning & Janitorial Services"; };
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("Resume must be a PDF, DOC or DOCX file.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Resume must be under 8MB.");
      return;
    }
    setResume(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.position) {
      toast.error("Please fill in your name, email, phone and position.");
      return;
    }
    if (!form.no_criminal_background) {
      toast.error("Please confirm the background statement to apply.");
      return;
    }
    setLoading(true);
    try {
      let resume_path = null, resume_filename = null;
      if (resume) {
        const fd = new FormData();
        fd.append("file", resume);
        const up = await axios.post(`${API}/applications/resume`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        resume_path = up.data.resume_path;
        resume_filename = up.data.resume_filename;
      }
      await axios.post(`${API}/applications`, {
        ...form,
        has_transportation: form.has_transportation === "yes" ? true : form.has_transportation === "no" ? false : null,
        work_authorized: form.work_authorized === "yes" ? true : form.work_authorized === "no" ? false : null,
        resume_path,
        resume_filename,
      });
      setDone(true);
      toast.success("Application submitted! We'll be in touch.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-slate-300 rounded-sm px-4 py-3 focus:border-[#1c4f9c] focus:ring-1 focus:ring-[#1c4f9c] outline-none";
  const labelCls = "block text-sm font-semibold text-[#1c4f9c] mb-2";

  return (
    <div data-testid="careers-page" className="bg-white">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-[#1c4f9c] overflow-hidden" data-testid="careers-hero">
          <div className="absolute inset-0 grain-overlay opacity-[0.08]" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#ff6b70] mb-4">
              <Briefcase size={16} /> Careers
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05] max-w-3xl">
              Join the Elite Facility Solutions Team
            </h1>
            <p className="mt-6 text-lg text-blue-100/90 max-w-2xl">
              We're always looking for reliable, hard-working cleaning professionals and handymen across Northeast Florida. Submit your information below and our team will reach out.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
              {PERKS.map((p) => (
                <span key={p} className="flex items-center gap-2 text-sm font-medium text-blue-50">
                  <CheckCircle2 size={16} className="text-[#ff6b70]" /> {p}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="py-20 sm:py-28 bg-[#f1f5f9]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            {done ? (
              <div data-testid="careers-success" className="bg-white border border-slate-200 rounded-sm p-10 text-center">
                <CheckCircle2 size={56} className="text-[#1c4f9c] mx-auto" />
                <h2 className="mt-5 text-3xl font-bold text-[#1c4f9c]">Application received!</h2>
                <p className="mt-3 text-slate-600">Thank you for your interest in joining Elite Facility Solutions. If your experience is a match, our hiring team will contact you soon.</p>
                <button onClick={() => navigate("/")} data-testid="careers-home-btn" className="mt-8 inline-flex items-center gap-2 bg-[#e02329] text-white font-bold py-3.5 px-7 rounded-sm hover:bg-[#c81e24] transition-colors">
                  <ArrowLeft size={18} /> Back to Home
                </button>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-sm p-8 sm:p-10">
                <h2 className="text-2xl font-bold text-[#1c4f9c]">Employment Application</h2>
                <p className="mt-2 text-sm text-slate-500">Fields marked * are required.</p>
                <form onSubmit={submit} className="mt-8 space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <input data-testid="app-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="John Smith" />
                    </div>
                    <div>
                      <label className={labelCls}>Phone *</label>
                      <input data-testid="app-phone" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(786) 918-1367" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input data-testid="app-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                    </div>
                    <div>
                      <label className={labelCls}>Position *</label>
                      <select data-testid="app-position" value={form.position} onChange={update("position")} className={`${inputCls} bg-white`}>
                        <option value="">Select a position</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Short Bio / About You</label>
                    <textarea data-testid="app-bio" value={form.bio} onChange={update("bio")} rows={3} className={`${inputCls} resize-none`} placeholder="Tell us a bit about your experience and what you bring to the team." />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Years of Experience</label>
                      <select data-testid="app-experience" value={form.years_experience} onChange={update("years_experience")} className={`${inputCls} bg-white`}>
                        <option value="">Select</option>
                        {["Less than 1 year", "1–3 years", "3–5 years", "5+ years"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Availability</label>
                      <select data-testid="app-availability" value={form.availability} onChange={update("availability")} className={`${inputCls} bg-white`}>
                        <option value="">Select</option>
                        {["Full-time", "Part-time", "Days", "Evenings", "Weekends", "Flexible"].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Reliable transportation?</label>
                      <select data-testid="app-transportation" value={form.has_transportation} onChange={update("has_transportation")} className={`${inputCls} bg-white`}>
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Authorized to work in the US?</label>
                      <select data-testid="app-authorized" value={form.work_authorized} onChange={update("work_authorized")} className={`${inputCls} bg-white`}>
                        <option value="">Select</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                  </div>

                  {/* Resume upload */}
                  <div>
                    <label className={labelCls}>Resume (PDF, DOC, DOCX — optional)</label>
                    <label data-testid="app-resume-label" className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-sm px-4 py-4 cursor-pointer hover:border-[#1c4f9c] transition-colors">
                      {resume ? <FileText size={20} className="text-[#1c4f9c]" /> : <Upload size={20} className="text-slate-400" />}
                      <span className="text-sm text-slate-600 truncate">{resume ? resume.name : "Click to upload your resume"}</span>
                      <input data-testid="app-resume-input" type="file" accept=".pdf,.doc,.docx" onChange={onFile} className="hidden" />
                    </label>
                  </div>

                  {/* Background confirmation */}
                  <button
                    type="button"
                    data-testid="app-background-check"
                    onClick={() => setForm((f) => ({ ...f, no_criminal_background: !f.no_criminal_background }))}
                    className={`w-full flex items-start gap-4 text-left p-4 rounded-sm border-2 transition-colors ${form.no_criminal_background ? "border-[#1c4f9c] bg-[#1c4f9c]/5" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <span className={`mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center rounded-sm border-2 transition-colors ${form.no_criminal_background ? "bg-[#1c4f9c] border-[#1c4f9c]" : "border-slate-300 bg-white"}`}>
                      {form.no_criminal_background && <CheckCircle2 size={16} className="text-white" />}
                    </span>
                    <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <ShieldCheck size={16} className="text-[#e02329] shrink-0" />
                      I confirm I have no criminal background and consent to a background check. *
                    </span>
                  </button>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="app-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#e02329] text-white font-bold py-4 px-8 rounded-sm hover:bg-[#c81e24] transition-colors disabled:opacity-70"
                  >
                    {loading ? <Loader2 className="animate-spin" size={18} /> : <Briefcase size={18} />}
                    {loading ? "Submitting..." : "Submit Application"}
                  </button>
                  <p className="text-center text-xs text-slate-400">
                    Questions? Call or text us at {CONTACT.phone}.
                  </p>
                </form>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <StickyCallBar />
    </div>
  );
}
